import { SortingState } from '@tanstack/react-table';

import { DataTableColumnDef, DataTableHeadFn, TextCell } from '@crossnokaye/ux-common';

import { ActionClearCell } from '@/features/alarms/alarm-tables/action-clear-cell';
import {
  COLUMN_ID_ACKNOWLEDGED_AT,
  COLUMN_ID_ACKNOWLEDGED_BY,
  COLUMN_ID_ACTION,
  COLUMN_ID_ACTIVE_FOR,
  COLUMN_ID_ALARM_SP,
  COLUMN_ID_CURRENT,
  COLUMN_ID_DATE,
  COLUMN_ID_DEVICE_NAME,
  COLUMN_ID_DEVICE_TYPE,
  COLUMN_ID_ISSUE,
  COLUMN_ID_RESOLVED_AT,
  COLUMN_ID_SEVERITY,
  COLUMN_ID_STATUS,
} from '@/features/alarms/alarm-tables/constants';
import { alarmDateSort, severitySort } from '@/features/alarms/alarm-tables/helpers';
import { SeverityCell } from '@/features/alarms/alarm-tables/severity-cell';
import { AlarmTableData } from '@/features/alarms/alarm-tables/types';
import { TZDateCell } from '@/features/alarms/alarm-tables/tz-date-cell';

export const initialSortingState: SortingState = [
  {
    id: 'date',
    desc: false,
  },
];

export const SeverityColumnDef: DataTableColumnDef<AlarmTableData, AlarmTableData> = {
  accessorKey: COLUMN_ID_SEVERITY,
  cell: SeverityCell,
  enableColumnFilter: false,
  enableSorting: true,
  header: DataTableHeadFn,
  headerText: 'Priority',
  id: COLUMN_ID_SEVERITY,
  meta: {
    mobile: {
      size: 55,
    },
  },
  sortingFn: severitySort,
};

export const DeviceNameColumnDef: DataTableColumnDef<AlarmTableData, AlarmTableData> = {
  accessorKey: COLUMN_ID_DEVICE_NAME,
  cell: TextCell,
  enableColumnFilter: false,
  enableSorting: true,
  header: DataTableHeadFn,
  headerText: 'Name',
  id: COLUMN_ID_DEVICE_NAME,
  size: 150,
};

export const DeviceTypeColumnDef: DataTableColumnDef<AlarmTableData, AlarmTableData> = {
  accessorKey: COLUMN_ID_DEVICE_TYPE,
  cell: TextCell,
  enableColumnFilter: false,
  enableSorting: true,
  header: DataTableHeadFn,
  headerText: 'Type',
  id: COLUMN_ID_DEVICE_TYPE,
  sortDescFirst: false,
};

export const IssueColumnDef: DataTableColumnDef<AlarmTableData, AlarmTableData> = {
  accessorKey: COLUMN_ID_ISSUE,
  cell: TextCell,
  enableColumnFilter: false,
  enableSorting: false,
  header: DataTableHeadFn,
  headerText: 'Issue',
  id: COLUMN_ID_ISSUE,
  meta: {
    growWidthFirst: true,
  },
};

export const CurrentColumnDef: DataTableColumnDef<AlarmTableData, AlarmTableData> = {
  accessorKey: COLUMN_ID_CURRENT,
  cell: TextCell,
  enableColumnFilter: false,
  enableSorting: false,
  header: DataTableHeadFn,
  headerText: 'Current',
  id: COLUMN_ID_CURRENT,
};

export const DateColumnDef: DataTableColumnDef<AlarmTableData, AlarmTableData> = {
  accessorKey: COLUMN_ID_DATE,
  cell: TZDateCell,
  enableColumnFilter: false,
  enableSorting: true,
  header: DataTableHeadFn,
  headerText: 'Date',
  id: COLUMN_ID_DATE,
  sortingFn: alarmDateSort,
};

export const StatusColumnDef: DataTableColumnDef<AlarmTableData, AlarmTableData> = {
  accessorKey: COLUMN_ID_STATUS,
  cell: TextCell,
  enableColumnFilter: false,
  enableSorting: false,
  header: DataTableHeadFn,
  headerText: 'Status',
  id: COLUMN_ID_STATUS,
};

export const ActiveForColumnDef: DataTableColumnDef<AlarmTableData, AlarmTableData> = {
  accessorKey: COLUMN_ID_ACTIVE_FOR,
  cell: TextCell,
  enableColumnFilter: false,
  enableSorting: false,
  header: DataTableHeadFn,
  headerText: 'Active For',
  id: COLUMN_ID_ACTIVE_FOR,
};

export const ActionColumnDef: DataTableColumnDef<AlarmTableData, AlarmTableData> = {
  accessorKey: COLUMN_ID_ACTION,
  cell: ActionClearCell,
  enableColumnFilter: false,
  enableResizing: false,
  enableSorting: false,
  header: DataTableHeadFn,
  headerText: '',
  id: COLUMN_ID_ACTION,
};

export const AcknowledgedAtColumnDef: DataTableColumnDef<AlarmTableData, AlarmTableData> = {
  accessorKey: COLUMN_ID_ACKNOWLEDGED_AT,
  cell: TZDateCell,
  enableColumnFilter: false,
  enableSorting: true,
  header: DataTableHeadFn,
  headerText: 'Acknowledged At',
  id: COLUMN_ID_ACKNOWLEDGED_AT,
  sortingFn: alarmDateSort,
};

export const AcknowledgedByColumnDef: DataTableColumnDef<AlarmTableData, AlarmTableData> = {
  accessorKey: COLUMN_ID_ACKNOWLEDGED_BY,
  cell: TextCell,
  enableColumnFilter: false,
  enableSorting: true,
  header: DataTableHeadFn,
  headerText: 'By',
  id: COLUMN_ID_ACKNOWLEDGED_BY,
};

export const ResolvedAtColumnDef: DataTableColumnDef<AlarmTableData, AlarmTableData> = {
  accessorKey: COLUMN_ID_RESOLVED_AT,
  cell: TZDateCell,
  enableColumnFilter: false,
  enableSorting: true,
  header: DataTableHeadFn,
  headerText: 'Resolved At',
  id: COLUMN_ID_RESOLVED_AT,
  sortingFn: alarmDateSort,
};

export const AlarmSetpointColumnDef: DataTableColumnDef<AlarmTableData, AlarmTableData> = {
  accessorKey: COLUMN_ID_ALARM_SP,
  cell: TextCell,
  enableColumnFilter: false,
  enableSorting: false,
  header: DataTableHeadFn,
  headerText: 'Alarm SP',
  id: COLUMN_ID_ALARM_SP,
};
